import {
  createFileRoute,
  getRouteApi,
  Link,
  useRouter,
} from "@tanstack/react-router"

import {
  loadTerminalCompanyFile,
  startTerminalMonitoring,
} from "@/actions/terminal-file-actions"
import { FileDetailSkeleton } from "@/components/terminal/detail/file-detail-skeleton"
import {
  FileMasthead,
  FilePane,
  TerminalFilePage,
} from "@/components/terminal/detail/terminal-file-page"
import { TerminalEmptyState } from "@/components/terminal/terminal-empty-state"
import type { TerminalPortfolioEntryView } from "@/lib/terminal/file-view"

// The shell already loaded the Portfolio rows; the pending, error and
// not-found states paint the masthead from the selected Company's row.
const terminalRoute = getRouteApi("/_terminal/terminal")

const useSelectedRow = () => {
  const { slug } = Route.useParams()
  const { rows } = terminalRoute.useLoaderData()
  return rows.find(({ company }) => company.slug === slug)
}

const useStartMonitoring = () => {
  const router = useRouter()
  return async (
    portfolioEntry: TerminalPortfolioEntryView,
    companyId: string,
    approvedOn: string | null
  ) => {
    await startTerminalMonitoring({
      data: { portfolioId: portfolioEntry.portfolioId, companyId, approvedOn },
    })
    await router.invalidate()
  }
}

// The selected Company's masthead from its Portfolio row, without dates; none
// when the row has no File.
const RowMasthead = () => {
  const row = useSelectedRow()
  const startMonitoring = useStartMonitoring()
  if (!row?.deliveredFile) return null
  return (
    <FileMasthead
      company={row.company}
      assessment={row.deliveredFile.assessment}
      isMonitoring={row.portfolioEntry.isMonitoring}
      onStartMonitoring={(approvedOn) =>
        startMonitoring(row.portfolioEntry, row.company.id, approvedOn)
      }
    />
  )
}

// Shown at once on a cold navigation (pendingMs 0): the masthead paints from
// the row, so the pane stays on the selected Company and only the body waits.
const CompanyFilePending = () => (
  <FilePane>
    <RowMasthead />
    <FileDetailSkeleton />
  </FilePane>
)

// Retry reloads the route: the boundary's own reset would rethrow the
// loader's stored error, while invalidate reruns the loader and gives the
// match a new reset key.
const CompanyFileError = () => {
  const router = useRouter()
  return (
    <FilePane>
      <RowMasthead />
      <TerminalEmptyState
        actionLabel="Retry"
        onAction={() => void router.invalidate()}
        className="min-h-60 justify-center"
      >
        This File could not be loaded.
      </TerminalEmptyState>
    </FilePane>
  )
}

const CompanyFile = () => {
  const { file } = Route.useLoaderData()
  const search = Route.useSearch()
  const startMonitoring = useStartMonitoring()
  if (!file)
    return (
      <FilePane>
        <RowMasthead />
        <div className="flex min-h-60 flex-col items-center justify-center gap-4">
          <p className="type-narrative">No File yet for this company.</p>
          <Link
            to="/terminal"
            search={search}
            className="type-micro text-terminal-accent hover:text-foreground"
          >
            Back to portfolio
          </Link>
        </div>
      </FilePane>
    )
  return (
    <TerminalFilePage
      key={file.company.id}
      file={file}
      onStartMonitoring={(approvedOn) =>
        startMonitoring(file.portfolioEntry, file.company.id, approvedOn)
      }
    />
  )
}

// One Company's Delivered File through the selected Portfolio.
export const Route = createFileRoute("/_terminal/terminal/$slug")({
  staticData: { shellMode: "companies" },
  // A File changes only when one of its Company's Runs ends, so a session
  // never refetches it within half an hour.
  staleTime: 30 * 60_000,
  preloadStaleTime: 30 * 60_000,
  // Show the pending state at once on a cold navigation; the default 1s
  // pendingMs left the prior File on screen. pendingMinMs keeps a fast load
  // from flashing, and a preloaded navigation skips the pending state.
  pendingMs: 0,
  pendingMinMs: 150,
  loaderDeps: ({ search }) => ({ portfolio: search.portfolio }),
  loader: async ({ context, deps, params }) => {
    if (!context.hasTerminalAccess || !deps.portfolio) return { file: null }
    const file = await loadTerminalCompanyFile({
      data: { portfolioSlug: deps.portfolio, slug: params.slug },
    })
    return { file }
  },
  pendingComponent: CompanyFilePending,
  errorComponent: CompanyFileError,
  component: CompanyFile,
})
